import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Filter, 
  Download, 
  Trash2, 
  RefreshCw, 
  Search,
  AlertCircle,
  Info,
  AlertTriangle,
  XCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { logger } from '../../utils/logger';

interface LogEntry {
  id: string;
  timestamp: Date;
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  source: string;
  data?: any;
}

const LogLevelColors = {
  debug: 'text-slate-500 bg-slate-100',
  info: 'text-blue-600 bg-blue-100',
  warn: 'text-amber-600 bg-amber-100',
  error: 'text-red-600 bg-red-100'
};

const LogLevelIcons = {
  debug: Info,
  info: Info,
  warn: AlertTriangle,
  error: XCircle
};

export const SystemLogs: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogEntry[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<Set<string>>(new Set(['info', 'warn', 'error']));
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Carregar logs reais do logger
  useEffect(() => {
    const loadLogs = () => {
      const allLogs = logger.getAllLogs();
      setLogs(allLogs);
    };

    loadLogs();
  }, []);

  // Filtrar logs
  useEffect(() => {
    let filtered = logs.filter(log => 
      selectedLevels.has(log.level) &&
      (searchTerm === '' || 
       log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
       log.source.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredLogs(filtered);
  }, [logs, selectedLevels, searchTerm]);


  const handleLevelToggle = (level: string) => {
    const newLevels = new Set(selectedLevels);
    if (newLevels.has(level)) {
      newLevels.delete(level);
    } else {
      newLevels.add(level);
    }
    setSelectedLevels(newLevels);
  };

  const handleClearLogs = () => {
    if (window.confirm('Tem certeza que deseja limpar todos os logs?')) {
      logger.clearLogs();
      setLogs([]);
    }
  };

  const handleExportLogs = () => {
    const logText = filteredLogs.map(log => 
      `[${log.timestamp.toISOString()}] ${log.level.toUpperCase()} [${log.source}]: ${log.message}`
    ).join('\n');
    
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    
    // Recarregar logs do logger
    const allLogs = logger.getAllLogs();
    setLogs(allLogs);
    
    setIsLoading(false);
  };

  const getLogCount = (level: string) => {
    return logs.filter(log => log.level === level).length;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-slate-900">PlayKids</h1>
                <p className="text-xs text-slate-500">Logs do Sistema</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="flex items-center gap-2 px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Atualizar
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Controls */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar nos logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Level Filters */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700">Filtros:</span>
              {Object.entries(LogLevelColors).map(([level, _]) => {
                const Icon = LogLevelIcons[level as keyof typeof LogLevelIcons];
                const count = getLogCount(level);
                return (
                  <button
                    key={level}
                    onClick={() => handleLevelToggle(level)}
                    className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      selectedLevels.has(level)
                        ? LogLevelColors[level as keyof typeof LogLevelColors]
                        : 'text-slate-500 bg-slate-100 hover:bg-slate-200'
                    }`}
                  >
                    <Icon className="w-3 h-3" />
                    {level}
                    <span className="ml-1">({count})</span>
                  </button>
                );
              })}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportLogs}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Exportar
              </button>
              <button
                onClick={handleClearLogs}
                className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Limpar
              </button>
            </div>
          </div>

          {/* Log Count */}
          <div className="mt-4 flex items-center gap-2">
            <span className="text-sm text-slate-500">
              {filteredLogs.length} de {logs.length} logs
            </span>
          </div>
        </div>

        {/* Logs Display */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">Logs em Tempo Real</h3>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {filteredLogs.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Nenhum log encontrado com os filtros aplicados</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {filteredLogs.map((log) => {
                  const Icon = LogLevelIcons[log.level];
                  return (
                    <div key={log.id} className="p-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className={`p-1 rounded-full ${LogLevelColors[log.level]}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-slate-900">
                              {log.message}
                            </span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${LogLevelColors[log.level]}`}>
                              {log.level.toUpperCase()}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {log.timestamp.toLocaleString('pt-BR')}
                            </span>
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              {log.source}
                            </span>
                          </div>
                          {log.data && (
                            <details className="mt-2">
                              <summary className="text-xs text-slate-600 cursor-pointer hover:text-slate-800">
                                Ver dados adicionais
                              </summary>
                              <pre className="mt-2 p-2 bg-slate-100 rounded text-xs overflow-x-auto">
                                {JSON.stringify(log.data, null, 2)}
                              </pre>
                            </details>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
